import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SendPhoneVerificationCodeRequest } from 'src/app/models/phoneVerification.model';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { UserService } from 'src/app/services/user.service';
import {
    SMS_CODE_EXPIRY_SECONDS,
    SMS_CODE_LENGTH,
    SMS_RESEND_COOLDOWN_SECONDS,
    SMS_VERIFY_MAX_ATTEMPTS,
} from 'src/app/shared/constants/smsOptIn.constants';
import { AccountOtpInputComponent } from '../account-otp-input/account-otp-input.component';

@Component({
    standalone: false,
    selector: 'app-account-sms-verify',
    templateUrl: './account-sms-verify.component.html',
    styleUrls: ['./account-sms-verify.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class AccountSmsVerifyComponent implements OnInit, OnDestroy {
    @Input() phoneNumber = '';
    @Input({ required: true }) consent!: SendPhoneVerificationCodeRequest;
    @Output() cancelled = new EventEmitter<void>();
    @Output() verified = new EventEmitter<void>();
    @Output() changeNumber = new EventEmitter<void>();

    @ViewChild(AccountOtpInputComponent)
    private otpInput?: AccountOtpInputComponent;

    readonly codeLength = SMS_CODE_LENGTH;

    code = '';
    canVerify = false;
    verifying = false;
    sending = false;
    codeInvalid = false;
    attemptsRemaining = SMS_VERIFY_MAX_ATTEMPTS;
    locked = false;
    resendCooldown = 0;
    resendCountdownDisplay = '';
    expiresIn = SMS_CODE_EXPIRY_SECONDS;
    expiresInDisplay = '';

    private resendTimer?: ReturnType<typeof setInterval>;
    private expiryTimer?: ReturnType<typeof setInterval>;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private userService: UserService,
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.startResendCooldown();
        this.startExpiryTimer();
    }

    onCodeChange(code: string): void {
        this.code = code;
        if (code) this.codeInvalid = false;
        this.refreshCanVerify();
    }

    verify(): void {
        if (!this.canVerify) return;

        this.verifying = true;
        this.refreshCanVerify();
        this.userService
            .verifyPhoneCode(this.code)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.verifying = false;
                    this.clearExpiryTimer();
                    this.verified.emit();
                },
                error: (err: HttpErrorResponse) => {
                    this.verifying = false;
                    this.codeInvalid = true;
                    this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);

                    const serverMessage = typeof err.error?.message === 'string' ? (err.error.message as string) : '';
                    if (serverMessage.toLowerCase().includes('too many') || this.attemptsRemaining === 0) {
                        this.locked = true;
                        this.clearExpiryTimer();
                    }
                    this.otpInput?.clear();
                    this.code = '';
                    this.refreshCanVerify();
                },
            });
    }

    resend(): void {
        if (this.locked) {
            this.changeNumber.emit();
            return;
        }
        if (this.resendCooldown > 0 || this.sending) return;

        this.sending = true;
        this.userService
            .sendPhoneVerificationCode(this.consent)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.sending = false;
                    this.resetVerifyState();
                    this.startResendCooldown();
                    this.startExpiryTimer();
                    this.toastService.showSuccess('A new code was sent.', true);
                },
                error: (err: HttpErrorResponse) => {
                    this.sending = false;
                    this.toastService.showError(
                        err,
                        this.translateService.getDynamicTranslation('Failed to resend the code.'),
                    );
                },
            });
    }

    ngOnDestroy(): void {
        this.clearResendTimer();
        this.clearExpiryTimer();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private refreshCanVerify(): void {
        this.canVerify = this.code.length === SMS_CODE_LENGTH && !this.verifying && !this.locked && this.expiresIn > 0;
    }

    private resetVerifyState(): void {
        this.code = '';
        this.codeInvalid = false;
        this.attemptsRemaining = SMS_VERIFY_MAX_ATTEMPTS;
        this.locked = false;
        this.otpInput?.clear();
        this.refreshCanVerify();
    }

    private startResendCooldown(): void {
        this.resendCooldown = SMS_RESEND_COOLDOWN_SECONDS;
        this.resendCountdownDisplay = this.formatCountdown(this.resendCooldown);
        this.clearResendTimer();
        this.resendTimer = setInterval(() => {
            this.resendCooldown = Math.max(0, this.resendCooldown - 1);
            this.resendCountdownDisplay = this.formatCountdown(this.resendCooldown);
            if (this.resendCooldown === 0) this.clearResendTimer();
        }, 1000);
    }

    private clearResendTimer(): void {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = undefined;
        }
    }

    private startExpiryTimer(): void {
        this.expiresIn = SMS_CODE_EXPIRY_SECONDS;
        this.expiresInDisplay = this.formatCountdown(this.expiresIn);
        this.clearExpiryTimer();
        this.expiryTimer = setInterval(() => {
            this.expiresIn = Math.max(0, this.expiresIn - 1);
            this.expiresInDisplay = this.formatCountdown(this.expiresIn);
            if (this.expiresIn === 0) {
                this.clearExpiryTimer();
                this.refreshCanVerify();
            }
        }, 1000);
    }

    private clearExpiryTimer(): void {
        if (this.expiryTimer) {
            clearInterval(this.expiryTimer);
            this.expiryTimer = undefined;
        }
    }

    private formatCountdown(totalSeconds: number): string {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
}
